/* eslint-disable no-param-reassign */
/* global G */
import { curry, isArr, isObj } from 'lib/util'

/**
 * Traverses all `configuration` items and returns the one for which `callback` returns `true`.
 *
 * @param {DynamicItemConfig|DynamicItemConfig[]} configuration   base items configuration
 * @param {DynamicItemValue} values                               current items' values
 * @param {function(DynamicItemConfig, number): boolean} callback executed for each item
 * @returns {[DynamicItemConfig, any]}                             an array with two items: the found
 *                                                                item and its value
 */
const deepFind = (configuration, values = {}, callback = () => {}) => configuration
  .reduce(([foundItem, value], item, index) => {
    if (foundItem) {
      return [foundItem, value]
    }

    if (callback(item, index)) {
      return [item, values?.[item.key]]
    }

    if (item) {
      if (isObj(item)) {
        return deepFind(Object.values(item), values?.[item.key], callback)
      }
      if (isArr(item)) {
        return deepFind(item, values, callback)
      }
    }

    return [null, null]
  }, [null, null])

/**
 * Returns `true` only if all `conditions` are fulfilled; that is, all properties and values defined
 * in them are found in the corresponding elements.
 *
 * @param {DynamicFormState} conditions
 * @param {DynamicItemConfig[]} configuration
 * @param {DynamicItemValue} values
 * @returns {boolean}
 */
const all = (conditions, configuration, values) => Object.keys(conditions).every((key) => {
  const condition = conditions[key]
  const [item, value] = deepFind(configuration, values, i => i?.key && i.key === key)

  if (!item) {
    return false
  }

  if (isObj(condition)) {
    const target = { ...item, value }
    return Object.keys(condition).every(name => condition[name] === target[name])
  }
  return condition === value
})

/**
 * Returns `true` if at least one of the passed `conditions` is fulfilled; that is, properties and
 * values defined in them are found in at least one of the corresponding elements.
 *
 * @param {DynamicFormState} conditions
 * @param {DynamicItemConfig[]} configuration
 * @param {DynamicItemValue} values
 * @returns {boolean}
 */
const some = (conditions, configuration, values) => Object.keys(conditions).some((key) => {
  const condition = conditions[key]
  const [item, value] = deepFind(configuration, values, i => i?.key && i.key === key)

  if (!item) {
    return false
  }

  if (isObj(condition)) {
    const target = { ...item, value }
    return Object.keys(condition).every(name => condition[name] === target[name])
  }
  return condition === value
})

/**
 * Returns `true` only if no `conditions` are fulfilled; that is, no properties with the values
 * defined in them are found in the corresponding elements.
 *
 * @param {DynamicFormState} conditions
 * @param {DynamicItemConfig[]} configuration
 * @param {DynamicItemValue} values
 * @returns {boolean}
 */
const none = (conditions, configuration, values) => Object.keys(conditions).every((key) => {
  const condition = conditions[key]
  const [item, value] = deepFind(configuration, values, i => i?.key && i.key === key)

  if (!item) {
    return true
  }

  if (isObj(condition)) {
    const target = { ...item, value }
    return !item || Object.keys(condition).every(name => condition[name] !== target[name])
  }
  return condition !== value
})

const checkGroup = { all, some, none }

/**
 * Checks whether all conditions in `effect` are met and, if so, returns the configuration defined
 * in the `effect.apply` block.
 *
 * @param {DynamicItemEffect & DynamicFormState} effect
 * @param {DynamicItemConfig[]} configuration
 * @param {DynamicItemValue} values
 * @returns {*|{}}
 */
const checkEffect = (configuration = [], effect = {}, values = {}) => {
  const [groups, rest] = Object.keys(effect.if).reduce(([conditions, items], key) => {
    key in checkGroup
      ? conditions[key] = effect.if[key]
      : items[key] = effect.if[key]
    return [conditions, items]
  }, [{}, {}])

  const pass = all(rest, configuration, values) && Object.keys(groups).every((key) => {
    const group = checkGroup[key]
    return group ? group(effect.if[key], configuration, values) : true
  })

  return pass ? effect.apply : {}
}

/**
 * Processes items according to configured field effects until all state conditions match.
 *
 * @param {DynamicItemConfig[]} configuration base items configuration
 * @param {DynamicItemValue} [values]         current items' values
 * @param {DynamicItemConfig[]} [items]       current array of configuration items
 * @param {{ value: boolean }} [changed]      internal change monitor
 * @returns {DynamicItemConfig[]}
 */
const processItems = (
  configuration = [],
  values = {},
  items = configuration,
  changed = { value: false },
) => {
  const newItems = items.reduce((acc, item) => {
    const props = item.effects?.reduce((agg, effect) => {
      const effectProps = checkEffect(configuration, effect, values)
      return { ...agg, ...effectProps }
    }, {}) || {}

    changed.value ||= Object.keys(props).some(key => props[key] !== item[key])

    const children = item?.items && processItems(configuration, values, item.items, changed)
    const field = { ...item, ...props, ...children ? { items: children } : {} }

    return [...acc, field]
  }, [])

  return changed.value ? processItems(newItems, values) : newItems
}

/**
 * Copies `error` and `errorText` properties from `sourceItems` to `targetItems`.
 *
 * @param {DynamicItemConfig[]} [sourceItems] items to copy errors from
 * @param {DynamicItemConfig[]} [targetItems] items to copy errors to
 */
const transferItemsErrors = (sourceItems = [], targetItems = []) => targetItems
  .map((targetItem) => {
    const sourceItem = sourceItems.find(item => item.key === targetItem.key)
    const { error, errorText } = sourceItem || {}

    const newItems = sourceItem?.items && targetItem?.items
      && transferItemsErrors(sourceItem.items, targetItem.items)
    const items = newItems?.length ? { items: newItems } : {}

    return { ...targetItem, ...items, error, errorText }
  })

/**
 * Persists value, configuration and processed items in corresponding component's state.
 *
 * Returns processed items, so that there is no need to update the UI for them.
 *
 * @type {Gaia.AppModule.EventHandler}
 */
const persistDynamicForm = async (module, component, event) => {
  const state = component[G.STATE]
  const { configuration, value } = event.detail || {}

  state.value = value
  state.configuration = configuration
  state.items = configuration && isArr(configuration)
    ? transferItemsErrors(state.items, processItems(configuration, value))
    : []

  return state.items
}

export default curry(persistDynamicForm)
